import React, { useState } from 'react';
import classNames from 'classnames';

import { Menu, MenuContent, MenuItem, MenuLabel } from '../../../../components';
import { useLanguage } from '../../../../context/languageContext';

import css from './LanguageMenu.module.css';

const LanguageOptions = ({ className, onSelect }) => {
  const {
    currentLanguage,
    supportedLanguages,
    changeLanguage,
    updateInProgress,
    updateError,
  } = useLanguage();

  const handleSelect = language => {
    onSelect?.();
    changeLanguage(language).catch(() => {
      // The local selection remains active if profile persistence fails.
    });
  };

  return (
    <div className={className} aria-busy={updateInProgress}>
      {supportedLanguages.map(language => {
        const isSelected = language.code === currentLanguage;
        return (
          <button
            key={language.code}
            type="button"
            className={classNames(css.languageOption, {
              [css.languageOptionSelected]: isSelected,
            })}
            onClick={() => handleSelect(language.code)}
            disabled={updateInProgress}
            aria-pressed={isSelected}
          >
            <span>{language.label}</span>
            <span className={css.optionCode} aria-hidden="true">
              {language.shortLabel}
            </span>
          </button>
        );
      })}
      {updateError ? (
        <p className={css.saveError} role="status">
          Could not save preference
        </p>
      ) : null}
    </div>
  );
};

const LanguageMenu = props => {
  const { mobile = false } = props;
  const [isOpen, setIsOpen] = useState(false);
  const { language, multilanguageEnabled } = useLanguage();

  if (!multilanguageEnabled) {
    return null;
  }

  if (mobile) {
    return (
      <div className={css.mobileRoot} aria-label="Language">
        <span className={css.mobileTitle}>Language</span>
        <LanguageOptions className={css.mobileOptions} />
      </div>
    );
  }

  return (
    <Menu
      className={css.desktopRoot}
      contentPosition="left"
      isOpen={isOpen}
      onToggleActive={setIsOpen}
      skipFocusOnNavigation
    >
      <MenuLabel
        id="language-menu-label"
        className={css.languageMenuLabel}
        isOpenClassName={css.languageMenuOpen}
        ariaLabel={`Select language. Current language: ${language.label}`}
      >
        <span>{language.shortLabel}</span>
        <span className={css.chevron} aria-hidden="true" />
      </MenuLabel>
      <MenuContent className={css.languageMenuContent}>
        <MenuItem key="language-options" className={css.languageMenuItem}>
          <LanguageOptions
            className={css.desktopOptions}
            onSelect={() => setIsOpen(false)}
          />
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

export default LanguageMenu;
